import type { ExpressionSpecification } from 'mapbox-gl';

export type SeasonCropProperty = 'summer_crop_id' | 'winter_crop_id';

export const SYNTHETIC_FIELD_CROP_STYLE_ID_BASE = 100_000;

export type FieldLayerCropStyle = {
    id?: string | number;
    dataScienceId?: number;
    color?: string;
};

export function normalizeFieldCropIdKey(id: string | number | undefined | null): string | null {
    if (id == null) return null;
    const s = String(id).trim().toLowerCase();
    return s.length > 0 ? s : null;
}

/** Stable season-property ids for map paint/filter when API dataScienceId is missing. */
export function buildCropSeasonStyleIdLookup(
    crops: { id?: string | number; dataScienceId?: number }[],
): Map<string, number> {
    const map = new Map<string, number>();
    let synthetic = 0;
    for (const crop of crops) {
        const key = normalizeFieldCropIdKey(crop.id);
        if (!key || map.has(key)) continue;
        map.set(key, crop.dataScienceId ?? SYNTHETIC_FIELD_CROP_STYLE_ID_BASE + ++synthetic);
    }
    return map;
}

export function seasonStyleIdForCrop(
    crop: { id?: string | number; dataScienceId?: number },
    lookup: Map<string, number>,
): number | undefined {
    const key = normalizeFieldCropIdKey(crop.id);
    if (key && lookup.has(key)) {
        return lookup.get(key);
    }
    return crop.dataScienceId;
}

export function cropForFieldProperties<T extends FieldLayerCropStyle>(
    crops: T[],
    props: Record<string, unknown> | undefined | null,
    seasonCropProperty: SeasonCropProperty,
): T | undefined {
    if (!props || crops.length === 0) return undefined;

    const cropIdKey = normalizeFieldCropIdKey(props['crop_id'] as string | number | undefined);
    if (cropIdKey) {
        const byCropId = crops.find((c) => normalizeFieldCropIdKey(c.id) === cropIdKey);
        if (byCropId) return byCropId;
    }

    const seasonValue = props[seasonCropProperty];
    if (seasonValue != null && seasonValue !== '' && seasonValue !== 0 && seasonValue !== '0') {
        const lookup = buildCropSeasonStyleIdLookup(crops);
        return crops.find((c) => seasonStyleIdForCrop(c, lookup) === Number(seasonValue));
    }

    return undefined;
}

export function buildCropIdColorMatch(crops: FieldLayerCropStyle[]): ExpressionSpecification | null {
    const match: unknown[] = ['match', ['downcase', ['to-string', ['get', 'crop_id']]]];
    let hasKeys = false;
    for (const crop of crops) {
        const key = normalizeFieldCropIdKey(crop.id);
        if (!key) continue;
        hasKeys = true;
        match.push(key, crop.color ?? '#FDFDFD');
    }
    if (!hasKeys) return null;
    match.push('#FDFDFD');
    return match as ExpressionSpecification;
}

export function buildSeasonIdColorMatch(
    crops: FieldLayerCropStyle[],
    seasonCropProperty: SeasonCropProperty,
): ExpressionSpecification | null {
    const lookup = buildCropSeasonStyleIdLookup(crops);
    const match: unknown[] = ['match', ['to-number', ['get', seasonCropProperty]]];
    let hasKeys = false;
    for (const crop of crops) {
        const styleId = seasonStyleIdForCrop(crop, lookup);
        if (styleId == null) continue;
        hasKeys = true;
        match.push(styleId, crop.color ?? '#FDFDFD');
    }
    if (!hasKeys) return null;
    match.push('#FDFDFD');
    return match as ExpressionSpecification;
}

export function buildFieldCropFilterParts(
    crops: FieldLayerCropStyle[],
    seasonCropProperty: SeasonCropProperty,
): ExpressionSpecification[] {
    const lookup = buildCropSeasonStyleIdLookup(crops);
    const parts: ExpressionSpecification[] = [];
    for (const crop of crops) {
        const cropKey = normalizeFieldCropIdKey(crop.id);
        const styleId = seasonStyleIdForCrop(crop, lookup);
        const seasonMatch: ExpressionSpecification | null =
            styleId != null
                ? (['==', ['to-number', ['get', seasonCropProperty]], styleId] as ExpressionSpecification)
                : null;
        const cropIdMatch: ExpressionSpecification | null = cropKey
            ? (['==', ['downcase', ['to-string', ['get', 'crop_id']]], cropKey] as ExpressionSpecification)
            : null;

        if (cropIdMatch && seasonMatch) {
            parts.push(['any', cropIdMatch, seasonMatch]);
        } else if (cropIdMatch) {
            parts.push(cropIdMatch);
        } else if (seasonMatch) {
            parts.push(seasonMatch);
        }
    }
    return parts;
}
